import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchData, updateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import Button from '@mui/material/Button';
import { CircularProgress, Fade, Tooltip, Chip, Select, MenuItem, FormControl, Collapse, Paper } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import CopyrightIcon from '@mui/icons-material/Copyright';
import BranchCreateModal from './BranchCreateModal';
import BranchEditModal from './BranchEditModal';

// Helper function to display field values with placeholder for null/empty
const displayValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'Not specified';
    }
    return value;
};

// BranchDetailCard component for displaying expanded branch details
const BranchDetailCard = ({ branch }) => {
    const fieldLabel = { fontSize: '10px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.4px' };
    const fieldValue = { margin: '2px 0 8px 0', fontSize: '12px', color: '#374151' };
    const card = { background: '#f8f9fa', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0' };
    const cardHeader = {
        display: 'flex', alignItems: 'center', gap: '6px',
        marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #2C3E6E'
    };

    return (
        <div style={{
            background: '#f0f4ff',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            padding: '12px',
            border: '1px solid #e2e8f0',
            borderTop: 'none',
        }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>

                {/* Contact Information */}
                <div style={card}>
                    <div style={cardHeader}>
                        <PhoneIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Contact Information</span>
                    </div>
                    <span style={fieldLabel}>Primary Contact</span>
                    <p style={fieldValue}>{displayValue(branch.ContactNumber1)}</p>
                    <span style={fieldLabel}>Secondary Contact</span>
                    <p style={fieldValue}>{displayValue(branch.ContactNumber2)}</p>
                    <span style={fieldLabel}>Alternate Contact</span>
                    <p style={fieldValue}>{displayValue(branch.ContactNumber3)}</p>
                    <span style={fieldLabel}>Email</span>
                    <p style={{ ...fieldValue, marginBottom: 0 }}>{displayValue(branch.Email)}</p>
                </div>

                {/* Address Details */}
                <div style={card}>
                    <div style={cardHeader}>
                        <LocationOnIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Address Details</span>
                    </div>
                    <span style={fieldLabel}>Address Line 1</span>
                    <p style={fieldValue}>{displayValue(branch.AddressLine1)}</p>
                    <span style={fieldLabel}>Address Line 2</span>
                    <p style={fieldValue}>{displayValue(branch.AddressLine2)}</p>
                    <span style={fieldLabel}>Location</span>
                    <p style={fieldValue}>{displayValue(branch.Location)}</p>
                    <span style={fieldLabel}>Pin Code</span>
                    <p style={fieldValue}>{displayValue(branch.PinCode)}</p>
                    <span style={fieldLabel}>State Code ID</span>
                    <p style={{ ...fieldValue, marginBottom: 0 }}>{displayValue(branch.StateCodeId)}</p>
                </div>

                {/* Tax Information */}
                <div style={card}>
                    <div style={cardHeader}>
                        <ReceiptIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tax Information</span>
                    </div>
                    <span style={fieldLabel}>GST Number</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.GSTNumber)}</p>
                    <span style={fieldLabel}>PAN Number</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace', marginBottom: 0 }}>{displayValue(branch.PanNumber)}</p>
                </div>

                {/* Configuration Settings */}
                <div style={card}>
                    <div style={cardHeader}>
                        <SettingsIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Configuration Settings</span>
                    </div>
                    <span style={fieldLabel}>E-Invoicing</span>
                    <div style={{ marginBottom: '8px' }}>
                        <Chip label={branch.IsEInvoicingEnabled ? 'Enabled' : 'Disabled'} size="small"
                            style={{ height: '18px', fontSize: '10px', fontWeight: '600',
                                background: branch.IsEInvoicingEnabled ? '#10b981' : '#ef4444', color: 'white' }} />
                    </div>
                    <span style={fieldLabel}>E-Waybill</span>
                    <div style={{ marginBottom: '8px' }}>
                        <Chip label={branch.IsEWaybillEnabled ? 'Enabled' : 'Disabled'} size="small"
                            style={{ height: '18px', fontSize: '10px', fontWeight: '600',
                                background: branch.IsEWaybillEnabled ? '#10b981' : '#ef4444', color: 'white' }} />
                    </div>
                    <span style={fieldLabel}>B2B Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.BToBPrefix)}</p>
                    <span style={fieldLabel}>B2C Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace', marginBottom: 0 }}>{displayValue(branch.BToCPrefix)}</p>
                    <span style={fieldLabel}>Quotation Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.QuotationPrefix)}</p>
                    <span style={fieldLabel}>Sale Order Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.SaleOrderPrefix)}</p>
                    <span style={fieldLabel}>Purchase Order Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.PurchaseOrderPrefix)}</p>
                    <span style={fieldLabel}>Purchase Return Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.PurchaseReturnPrefix)}</p>
                    <span style={fieldLabel}>Sale Return Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace', marginBottom: 0 }}>{displayValue(branch.SaleReturnPrefix)}</p>
                </div>

                {/* Document Prefixes */}
                {/* <div style={card}>
                    <div style={cardHeader}>
                        <ReceiptIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Document Prefixes</span>
                    </div>
                    <span style={fieldLabel}>IGST Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.IGSTPrefix)}</p>
                    <span style={fieldLabel}>IGST Customer Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.IGSTCustomerPrefix)}</p>
                    <span style={fieldLabel}>Quotation Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.QuotationPrefix)}</p>
                    <span style={fieldLabel}>Sale Order Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.SaleOrderPrefix)}</p>
                    <span style={fieldLabel}>Purchase Order Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.PurchaseOrderPrefix)}</p>
                    <span style={fieldLabel}>Purchase Return Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace' }}>{displayValue(branch.PurchaseReturnPrefix)}</p>
                    <span style={fieldLabel}>Sale Return Prefix</span>
                    <p style={{ ...fieldValue, fontFamily: 'monospace', marginBottom: 0 }}>{displayValue(branch.SaleReturnPrefix)}</p>
                </div> */}

            </div>
        </div>
    );
};

const CompanyEdit = ({ match }) => {
    // Session validation
    useSessionValidation();
    
    const { isAuthenticated, companyId, logout, token, userId } = useAuth();
  const { logAudit } = useAuditLog();
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [formProgress, setFormProgress] = useState(0);
    const [openBranchModal, setOpenBranchModal] = useState(false);
    const [openBranchEditModal, setOpenBranchEditModal] = useState(false);
    const [selectedBranchForEdit, setSelectedBranchForEdit] = useState(null);
    const [isSavingBranch, setIsSavingBranch] = useState(false);
    const [expandedBranchId, setExpandedBranchId] = useState(null);

    // Toggle branch expansion - collapse if same branch clicked, expand new one otherwise
    const handleBranchRowClick = (branchId) => {
        setExpandedBranchId(prevId => prevId === branchId ? null : branchId);
    };

    useEffect(() => {
        setMounted(true);
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

    }, [isAuthenticated, companyId]);

    const [formData, setFormData] = useState([]);
    const [originalFormData, setOriginalFormData] = useState(null); // snapshot for audit diff
    const [branches, setBranches] = useState([
        // { id: 1, code: 'HQ01', name: 'Head Office', contact: '5551234567', status: 'Active' },
        // { id: 2, code: 'BR02', name: 'North Branch', contact: '5559871234', status: 'Active' },
        // { id: 3, code: 'BR03', name: 'South Branch', contact: '5555647890', status: 'Active' }
    ]);

    useEffect(() => {
        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    setFormData(response.response_data);
                    setOriginalFormData(response.response_data); // snapshot before any edits
                    setTimeout(() => calculateProgress(), 200);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            });
    }, []);

    // branch list
    useEffect(() => {
        fetchData('BranchApi', `/${companyId}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    setBranches(response.response_data);
                    setTimeout(() => calculateProgress(), 200);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value === 'true' ? true : e.target.type === 'checkbox' ? e.target.checked : value,
        }));

        // Calculate form completion progress
        setTimeout(() => calculateProgress(), 100);
    };

    const calculateProgress = () => {
        const requiredFields = ['Name', 'ContactNumber1', 'AddressLine1', 'Location'];
        const optionalFields = ['GSTNumber', 'ContactNumber2', 'ContactNumber3', 'Email', 'AddressLine2', 'PostalCode', 'CashOpening', 'FreightGSTRate'];

        let filledRequired = 0;
        let filledOptional = 0;

        requiredFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledRequired++;
            }
        });

        optionalFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledOptional++;
            }
        });

        const requiredWeight = 70;
        const optionalWeight = 30;

        const progress = (filledRequired / requiredFields.length) * requiredWeight +
            (filledOptional / optionalFields.length) * optionalWeight;

        setFormProgress(Math.round(progress));
    };


    const handleSubmit = event => {
        event.preventDefault();
        setIsLoading(true);

        const updatedFormData = { ...formData, UpdatedBy: userId };
        setFormData(updatedFormData);

        updateData('CompanyApi', `${companyId}`, updatedFormData, token)
            .then((response) => {
                console.log('Record updated successfully:', response.data);
                // Log only the changed fields
                logAudit(AUDIT_ACTION.UPDATE, 'CompanyApi', companyId, originalFormData, updatedFormData);
                setOriginalFormData(updatedFormData); // update snapshot to new state
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setIsLoading(false);
            });
    };

    const handleOpenBranchModal = () => {
        setOpenBranchModal(true);
    };

    const handleCloseBranchModal = () => {
        setOpenBranchModal(false);
    };

    const handleOpenBranchEditModal = (branch) => {
        setSelectedBranchForEdit(branch);
        setOpenBranchEditModal(true);
    };

    const handleCloseBranchEditModal = () => {
        setOpenBranchEditModal(false);
        setSelectedBranchForEdit(null);
    };

    const handleSaveBranch = async (branchData) => {
        setIsSavingBranch(true);
        try {
            // Use the complete form data from the modal
            const apiData = {
                CompanyId: companyId,
                Code: branchData.Code,
                Name: branchData.Name,
                AddressLine1: branchData.AddressLine1,
                AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1,
                ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3,
                Email: branchData.Email,
                GSTNumber: branchData.GSTNumber,
                PanNumber: branchData.PanNumber,
                Location: branchData.Location,
                PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BToBPrefix: branchData.BToBPrefix,
                BToCPrefix: branchData.BToCPrefix,
                IGSTPrefix: branchData.IGSTPrefix,
                IGSTCustomerPrefix: branchData.IGSTCustomerPrefix,
                QuotationPrefix: branchData.QuotationPrefix,
                SaleOrderPrefix: branchData.SaleOrderPrefix,
                PurchaseOrderPrefix: branchData.PurchaseOrderPrefix,
                PurchaseReturnPrefix: branchData.PurchaseReturnPrefix,
                SaleReturnPrefix: branchData.SaleReturnPrefix,
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsEInvoicingEnabled: branchData.IsEInvoicingEnabled,
                IsEWaybillEnabled: branchData.IsEWaybillEnabled,
                EinvoicePortalUserName: branchData.EinvoicePortalUserName,
                EinvoicePortalPassword: branchData.EinvoicePortalPassword,
                myGSTcafeCustomerID: branchData.myGSTcafeCustomerID,
                myGSTcafeApiID: branchData.myGSTcafeApiID,
                myGSTcafeApiSecret: branchData.myGSTcafeApiSecret,
                CreatedBy:userId,
            };

            const response = await postData('BranchApi', '', apiData, token);
            console.log('Create Branch response:', response);
            // Add to branches list and close modal on any successful response
            const newId = response?.response_data?.KOTId || response?.kotid || Date.now();
            setBranches(prevBranches => [...prevBranches, {
                id: newId,
                code: apiData.Code,
                name: apiData.Name,
                contact: apiData.ContactNumber1,
                status: 'Active'
            }]);
            logAudit(AUDIT_ACTION.CREATE, 'BranchApi', newId, null, {
                Name: apiData.Name,
                Code: apiData.Code,
                GSTNumber: apiData.GSTNumber,
                Location: apiData.Location,
            });
            handleCloseBranchModal();
        } catch (error) {
            console.error('Error creating branch:', error);
        } finally {
            setIsSavingBranch(false);
        }
    };

    const handleUpdateBranch = async (branchData) => {
        setIsSavingBranch(true);
        try {
            // Use the complete form data from the modal
            const apiData = {
                Id: branchData.Id,
                CompanyId: companyId,
                Code: branchData.Code,
                Name: branchData.Name,
                AddressLine1: branchData.AddressLine1,
                AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1,
                ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3,
                Email: branchData.Email,
                GSTNumber: branchData.GSTNumber,
                PanNumber: branchData.PanNumber,
                Location: branchData.Location,
                PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BToBPrefix: branchData.BToBPrefix,
                BToCPrefix: branchData.BToCPrefix,
                IGSTPrefix: branchData.IGSTPrefix,
                IGSTCustomerPrefix: branchData.IGSTCustomerPrefix,
                QuotationPrefix: branchData.QuotationPrefix,
                SaleOrderPrefix: branchData.SaleOrderPrefix,
                PurchaseOrderPrefix: branchData.PurchaseOrderPrefix,
                PurchaseReturnPrefix: branchData.PurchaseReturnPrefix,
                SaleReturnPrefix: branchData.SaleReturnPrefix,
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsEInvoicingEnabled: branchData.IsEInvoicingEnabled,
                IsEWaybillEnabled: branchData.IsEWaybillEnabled,
                EinvoicePortalUserName: branchData.EinvoicePortalUserName,
                EinvoicePortalPassword: branchData.EinvoicePortalPassword,
                myGSTcafeCustomerID: branchData.myGSTcafeCustomerID,
                myGSTcafeApiID: branchData.myGSTcafeApiID,
                myGSTcafeApiSecret: branchData.myGSTcafeApiSecret,
                ModifiedBy: userId,
                IsActive: true,
                CreatedBy: branchData.CreatedBy,
                CreatedDate: branchData.CreatedDate,
            };

            const response = await updateData('BranchApi', branchData.Id, apiData, token);
            if (response.responseCode === '1111') {
                // Update branches list
                setBranches(prevBranches => 
                    prevBranches.map(branch => 
                        branch.Id === branchData.Id 
                            ? { ...branch, ...apiData }
                            : branch
                    )
                );
                // Log only changed branch fields (compare original branchData vs apiData)
                logAudit(AUDIT_ACTION.UPDATE, 'BranchApi', branchData.Id, selectedBranchForEdit, apiData);
                handleCloseBranchEditModal();
                // Refresh the branch list to get updated data
                fetchData('BranchApi', `/${companyId}`, token)
                    .then((response) => {
                        if (response.responseCode == '1111') {
                            setBranches(response.response_data);
                        }
                    })
                    .catch((error) => {
                        console.error('Error fetching updated branch data:', error);
                    });
            } else {
                console.error('Error updating branch:', response.message);
            }
        } catch (error) {
            console.error('Error updating branch:', error);
        } finally {
            setIsSavingBranch(false);
        }
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
                    .premium-input {
                        transition: border-color 0.2s;
                        border: 1.5px solid #e5e7eb;
                        background: #ffffff;
                    }
                    .premium-input:focus {
                        border-color: #2C3E6E;
                        outline: none;
                        box-shadow: 0 0 0 3px rgba(44, 62, 110, 0.08);
                    }
                    .table-row:hover {
                        background: #f0f4ff !important;
                    }
                `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: 'Times New Roman',
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <h4 style={{
                            fontSize: '17px',
                            fontWeight: '700',
                            margin: 0,
                            letterSpacing: '-0.3px',
                            fontFamily: 'Times New Roman',
                            textTransform: 'uppercase',
                            color: 'black'
                        }}>
                            Company
                        </h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isLoading}
                                startIcon={isLoading ? null : <SaveIcon style={{ fontSize: '14px' }} />}
                                style={{
                                    background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    boxShadow: '0 2px 8px rgba(44, 62, 110, 0.4)',
                                    border: 'none',
                                    minHeight: '29px',
                                    textTransform: 'none',
                                }}
                            >
                                {isLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                            </Button>
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/Home"
                                startIcon={<ArrowBackIcon style={{ fontSize: '14px' }} />}
                                style={{
                                    borderColor: '#2C3E6E',
                                    color: '#2C3E6E',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    minHeight: '29px',
                                    textTransform: 'none',
                                }}
                            >
                                Dashboard
                            </Button>
                        </div>
                    </div>

                        {/* Two Column Layout */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '12px',
                            marginBottom: '10px'
                        }}>
                            {/* Left Column - Company Configuration */}
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                                    padding: '6px 12px',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <BusinessIcon style={{ fontSize: '16px', color: 'white' }} />
                                    <span style={{
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '11px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Company Configuration
                                    </span>
                                </div>
                                <div style={{
                                    padding: '12px',
                                    border: '1px solid #e2e8f0',
                                    borderTop: 'none',
                                    borderRadius: '0 0 8px 8px',
                                    background: 'white',
                                    minHeight: '200px'
                                }}>
                                    {/* Company Name */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            marginBottom: '6px',
                                            letterSpacing: '-0.01em'
                                        }}>
                                            Company Name
                                        </label>
                                        <input
                                            className="premium-input"
                                            name='Name'
                                            value={formData.Name}
                                            onChange={handleChange}
                                            type="text"
                                            placeholder="Enter your company name"
                                            required
                                            maxLength={50}
                                            style={{
                                                width: '100%',
                                                padding: '8px 8px',
                                                fontSize: '13px',
                                                borderRadius: '6px',
                                                outline: 'none',
                                                fontWeight: '500',
                                                color: '#1e293b',
                                                letterSpacing: '-0.01em'
                                            }}
                                        />
                                    </div>

                                    {/* Sales Calculation Type & Sales Method */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            marginBottom: '6px',
                                            letterSpacing: '-0.01em'
                                        }}>
                                            Sales Calculation Type
                                        </label>
                                        <FormControl fullWidth>
                                            <Select
                                                name="SalesCalculationType"
                                                value={formData.SalesCalculationType || 1}
                                                onChange={handleChange}
                                                className="premium-input"
                                                style={{
                                                    background: 'white',
                                                    fontSize: '13px',
                                                    height: '36px',
                                                    borderRadius: '6px',
                                                    fontWeight: '500',
                                                    color: '#1e293b'
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            borderRadius: '8px',
                                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                                                            border: '1px solid #e2e8f0',
                                                            marginTop: '2px'
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem value={1} style={{ fontSize: '14px', fontWeight: '500', padding: '8px 12px' }}>
                                                    1. For Inclusive GST
                                                </MenuItem>
                                                <MenuItem value={2} style={{ fontSize: '14px', fontWeight: '500', padding: '8px 12px' }}>
                                                    2. For Exclusive GST
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            marginBottom: '6px',
                                            letterSpacing: '-0.01em'
                                        }}>
                                            Sales Method
                                        </label>
                                        <FormControl fullWidth>
                                            <Select
                                                name="SalesMethod"
                                                value={formData.SalesMethod || 1}
                                                onChange={handleChange}
                                                className="premium-input"
                                                style={{
                                                    background: 'white',
                                                    fontSize: '13px',
                                                    height: '36px',
                                                    borderRadius: '6px',
                                                    fontWeight: '500',
                                                    color: '#1e293b'
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            borderRadius: '8px',
                                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                                                            border: '1px solid #e2e8f0',
                                                            marginTop: '2px'
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem value={1} style={{ fontSize: '14px', fontWeight: '500', padding: '8px 12px' }}>
                                                    1. For FIFO
                                                </MenuItem>
                                                <MenuItem value={2} style={{ fontSize: '14px', fontWeight: '500', padding: '8px 12px' }}>
                                                    2. For Batch Wise
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    </div>

                                </div>
                            </div>

                            {/* Right Column - Branch Management */}
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                                    padding: '6px 12px',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <AccountTreeIcon style={{ fontSize: '16px', color: 'white' }} />
                                        <span style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Branch Network
                                        </span>
                                    </div>
                                </div>
                                <div style={{
                                    padding: '12px',
                                    border: '1px solid #e2e8f0',
                                    borderTop: 'none',
                                    borderRadius: '0 0 8px 8px',
                                    background: 'white',
                                    minHeight: '200px'
                                }}>
                                    {/* Add New Branch Button */}
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon style={{ fontSize: '14px' }} />}
                                        onClick={handleOpenBranchModal}
                                        style={{
                                            background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                                            color: 'white',
                                            marginBottom: '10px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            padding: '4px 12px',
                                            textTransform: 'none',
                                            boxShadow: '0 2px 8px rgba(44, 62, 110, 0.4)',
                                            minHeight: '29px'
                                        }}
                                    >
                                        Add New Branch
                                    </Button>

                                    {/* Branch Table */}
                                    <TableContainer component={Paper} style={{
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        marginBottom: '10px'
                                    }}>
                                        <Table size="small" aria-label="branch table">
                                            <TableHead>
                                                <TableRow style={{
                                                    background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                                                    height: '27px'
                                                }}>
                                                    {['', 'ID', 'Code', 'Name', 'Contact', 'Status', 'Edit'].map((col) => (
                                                        <TableCell key={col} style={{
                                                            color: 'white',
                                                            fontWeight: '600',
                                                            fontSize: '11px',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            padding: '4px 8px',
                                                            height: '27px'
                                                        }}>
                                                            {col}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {branches.map((branch, index) => (
                                                    <React.Fragment key={branch.Id}>
                                                        <TableRow
                                                            className="table-row"
                                                            onClick={() => handleBranchRowClick(branch.Id)}
                                                            style={{
                                                                background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                                borderBottom: '2px solid #f0f0f0',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <TableCell style={{ padding: '6px 8px', width: '30px' }}>
                                                                {expandedBranchId === branch.Id
                                                                    ? <ExpandLessIcon style={{ fontSize: '16px', color: '#2C3E6E' }} />
                                                                    : <ExpandMoreIcon style={{ fontSize: '16px', color: '#2C3E6E' }} />
                                                                }
                                                            </TableCell>
                                                            <TableCell style={{ fontSize: '12px', padding: '6px 8px', color: '#2d3748' }}>{branch.Id}</TableCell>
                                                            <TableCell style={{ fontSize: '12px', padding: '6px 8px', color: '#2d3748', fontFamily: 'monospace' }}>{branch.Code}</TableCell>
                                                            <TableCell style={{ fontSize: '12px', padding: '6px 8px', color: '#2d3748', fontWeight: '600' }}>{branch.Name}</TableCell>
                                                            <TableCell style={{ fontSize: '12px', padding: '6px 8px', color: '#2d3748' }}>{branch.ContactNumber1}</TableCell>
                                                            <TableCell style={{ padding: '6px 8px' }}>
                                                                <Chip
                                                                    label={branch.IsActive ? 'Active' : 'Inactive'}
                                                                    size="small"
                                                                    style={{
                                                                        height: '18px',
                                                                        fontSize: '10px',
                                                                        fontWeight: '600',
                                                                        background: branch.IsActive
                                                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                                        color: 'white'
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell style={{ padding: '6px 8px' }}>
                                                                <Tooltip title="Edit Branch" arrow>
                                                                    <Button
                                                                        size="small"
                                                                        onClick={(e) => { e.stopPropagation(); handleOpenBranchEditModal(branch); }}
                                                                        style={{
                                                                            minWidth: '22px',
                                                                            height: '22px',
                                                                            borderRadius: '6px',
                                                                            color: 'white',
                                                                            padding: '2px',
                                                                            background: '#2C3E6E',
                                                                            boxShadow: '0 1px 4px rgba(44, 62, 110, 0.3)',
                                                                            border: 'none',
                                                                        }}
                                                                    >
                                                                        <EditIcon style={{ fontSize: '13px' }} />
                                                                    </Button>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow key={`detail-${branch.Id}`}>
                                                            <TableCell colSpan={7} style={{ padding: 0, border: 'none' }}>
                                                                <Collapse in={expandedBranchId === branch.Id} timeout={300}>
                                                                    <BranchDetailCard branch={branch} />
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </div>
                        </div>
                    </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    marginTop: '8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#6b7280'
                }}>
                    <CopyrightIcon style={{ fontSize: '13px' }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
                {/* New Branch Modal */}
                <BranchCreateModal
                    open={openBranchModal}
                    onClose={handleCloseBranchModal}
                    onSave={handleSaveBranch}
                    isSaving={isSavingBranch}
                />

                {/* Edit Branch Modal */}
                <BranchEditModal
                    open={openBranchEditModal}
                    onClose={handleCloseBranchEditModal}
                    onSave={handleUpdateBranch}
                    isSaving={isSavingBranch}
                    branchData={selectedBranchForEdit}
                />
            </div>
        </Fade>
    );
};

export default CompanyEdit;

